// Extracted from original/src/components/CashTransfers.tsx:1255-2186 (the "تسجيل حوالة جديدة" dialog, ADR-08 §2.1
// "copy, then cut logic"): the frame and header (:1258-1286), step 1 — settlement type, currency cards with their
// balance, direction (:1289-1474); step 2 — person and document, payment and accounts, the amount / rate / fee /
// commission / total grid, the live balance line (:1475-1922); step 3 — the vault-cash settlement of a foreign
// currency (:1923-2181).
// Kept: every class string, the steppers, the emoji and icons, the dynamic font size of the money inputs
// (`getDynamicFontSizeStyle` :347-354), the comma formatting and Eastern-digit conversion of typed numbers
// (`formatInputWithCommas` / `parseInputCommas` :141-173 → ../lib/format), the account `<select>` with its quick-search
// box, the PartnerOfficeSelector.
// Cut: every `useState` of business data (:183-291) → `values` / `onChange` and the other props (the container holds
// the form); `updateAmount` / `updateCommission` / `updateCommissionRate` / `updateTotalAmount` (:363-420, float
// arithmetic) → the container asks the server's calculator (`POST /transfers/quote`, ADR-02 §2.9); `getDefaultSettlementRate`
// with its hard-coded table (:216-253) and `computeSettlementCash` (:255-265) → pre-filled rates from the approved rate
// table and the settlement cash from the server preview (D-012, D-085); `getCurrencyAvailableBalance` over stored balances
// (:298-308) → `currencies[].balanceLabel` / `balanceAlert` from the ledger; `handleSubmit` / `handleStep2Next` /
// `executeSubmit` (:617-911) → `onNext` / `onSubmit` (the container validates, previews, asks the PIN, D-018);
// `generateRandomCode` (`ALF-` + `Math.random`, :357-359) → `onGenerate` (the server issues the code, D-087).
// Changed by decision: the code field is read-only for an outgoing transfer (D-087: server-generated, not editable) and
// typed for an incoming one; step 1 no longer switches the tab to "office" when the vault balance is zero and its
// warning is gone (D-079 / D-080: an outgoing transfer receives cash, a courier brings the cash of an incoming one —
// the server checks only the cash actually leaving, D05 T-17; V-32); on an incoming transfer through a partner the fee
// label says the partner pays it to us (D-083) and a "who pays our commission" switch appears (D-082) (V-33);
// step 3 gains the method (b) card "نفس العملة" (D-085 (b), valuation rate pre-filled from the approved buy rate) and,
// for an outgoing settlement, the cost-rate field pre-filled with the stock average (D-016, D-065) (V-35).
// Added: data-testid attributes.
import type { CSSProperties } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { AlertTriangle, Printer, SendHorizontal, X } from 'lucide-react';
import { PartnerOfficeSelector, type PartnerOfficeSelectorProps } from '../components/PartnerOfficeSelector.js';
import { formatWithCommas, parseCommas, toEnglishDigits } from '../lib/format.js';
import { useI18n } from '../i18n-context.js';
import type { CtDirection, CtPayment, CtTab } from './CashTransfers.js';

export type CtFormField =
  | 'clientName' | 'clientDocument' | 'destination' | 'code'
  | 'amount' | 'commissionRate' | 'partnerFee' | 'commission' | 'totalAmount'
  | 'settlementRate' | 'settlementCash' | 'costRate' | 'valuationRate';
export type CtFormValues = Readonly<Record<CtFormField, string>>;
/** Step 3: IQD / USD settlement (D-085 (a)) or the currency itself (D-085 (b), new). */
export type CtSettlement = 'IQD' | 'USD' | 'SELF';

export interface CtCurrencyChoice {
  readonly code: string;
  readonly flag: string;
  readonly label: string;
  readonly symbol: string;
  /** Formatted vault / stock balance (old `formatRemittanceNumber(cBal)`). */
  readonly balanceLabel: string;
  readonly zero: boolean;
}

export interface CtBalanceAlert {
  readonly state: 'zero' | 'deficit' | 'ok';
  readonly currencyName: string;
  readonly symbol: string;
  readonly balanceLabel: string;
  readonly requiredLabel: string;
  readonly deficitLabel: string;
}

export interface CtStep3Summary {
  readonly principalLabel: string;
  readonly commissionLabel: string;
  /** Credit / trust of a foreign currency: the commission at the settlement rate (old :2104-2114); method (b): its IQD value. */
  readonly commissionConvertedLabel: string | null;
  readonly partnerFeeLabel: string | null;
  readonly totalLabel: string;
  /** "1,656,400.00 د.ع" — the settlement cash entering (sent) or leaving (received) the vault. */
  readonly cashFlowLabel: string;
}

export interface TransferFormModalProps {
  readonly open: boolean;
  readonly step: 1 | 2 | 3;
  readonly onStep: (step: 1 | 2 | 3) => void;
  readonly tab: CtTab;
  readonly onTabChange: (tab: CtTab) => void;
  readonly currencies: readonly CtCurrencyChoice[];
  readonly currency: string;
  readonly onCurrency: (code: string) => void;
  readonly isForeign: boolean;
  readonly direction: CtDirection;
  readonly onDirection: (d: CtDirection) => void;
  readonly values: CtFormValues;
  readonly onChange: (field: CtFormField, raw: string) => void;
  readonly codeEditable: boolean;
  readonly onGenerate: () => void;
  readonly payment: CtPayment;
  readonly onPayment: (p: CtPayment) => void;
  readonly customerOptions: readonly { readonly id: string; readonly label: string }[];
  readonly customerId: string;
  readonly onCustomer: (id: string) => void;
  readonly onCustomerSearch: (text: string) => void;
  readonly showPartner: boolean;
  readonly partner: PartnerOfficeSelectorProps;
  /** Incoming through a partner (D-082 / D-083). */
  readonly partnerIncoming: boolean;
  readonly commissionPayer: 'partner' | 'beneficiary';
  readonly onCommissionPayer: (p: 'partner' | 'beneficiary') => void;
  readonly balanceAlert: CtBalanceAlert | null;
  readonly error: string | null;
  readonly busy: boolean;
  readonly onNext: () => void;
  readonly onSubmit: (print: boolean) => void;
  readonly onClose: () => void;
  // step 3
  readonly settlement: CtSettlement;
  readonly onSettlement: (s: CtSettlement) => void;
  readonly usdSettlementAvailable: boolean;
  readonly iqdVaultLabel: string;
  readonly usdVaultLabel: string;
  readonly selfStockLabel: string;
  /** "1 EUR = 1,640 د.ع" (quote unit of the currency, D-021). */
  readonly rateHeader: string;
  readonly quoteUnitLabel: string;
  readonly showCost: boolean;
  readonly summary: CtStep3Summary;
}

const getDynamicFontSizeStyle = (val: string, defaultSize: number = 24): CSSProperties => {
  const len = val ? val.length : 0;
  if (len <= 7) return { fontSize: `${defaultSize}px` };
  const diff = len - 7;
  const calculated = defaultSize - diff * 1.5;
  const size = Math.max(12, calculated);
  return { fontSize: `${size}px` };
};

export function TransferFormModal(p: TransferFormModalProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  const activeSubTab = p.tab;
  const currency = p.currency;
  const isForeignCurrency = p.isForeign;
  const v = p.values;
  const formatInputWithCommas = (val: string) => formatWithCommas(toEnglishDigits(val ?? ''));
  const typed = (field: CtFormField) => (e: { target: { value: string } }) => {
    const raw = parseCommas(toEnglishDigits(e.target.value));
    if (raw === '' || /^\d*\.?\d*$/.test(raw)) p.onChange(field, raw);
  };
  const settleSym = p.settlement === 'USD' ? '$' : p.settlement === 'IQD' ? 'د.ع' : (p.currencies.find((c) => c.code === currency)?.symbol ?? currency);

  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm" data-testid="transfer-form">
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 15 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 15 }}
            className="bg-white dark:bg-[#0d1117] rounded-[2.5rem] border border-slate-150 dark:border-[#30363d] w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl p-6 md:p-8"
          >
            <div className="flex justify-between items-center pb-5 border-b border-slate-100 dark:border-[#30363d] text-right">
              <div className="space-y-1">
                <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <SendHorizontal className="text-indigo-600" size={20} />
                  {activeSubTab === 'vault' ? t('ct.tab_vault') : t('ct.tab_office')}
                </h3>
                <p className="text-[10px] text-gray-400 font-semibold">{t('ct.form_desc')}</p>
              </div>
              <button
                type="button"
                onClick={p.onClose}
                className="text-slate-400 hover:text-slate-600 transition-colors p-2 bg-slate-50 dark:bg-[#161b22] rounded-xl border border-slate-100 dark:border-[#30363d]"
                data-testid="transfer-form-close"
              >
                <X size={18} />
              </button>
            </div>

            {p.error && (
              <div className="mt-4 p-4 rounded-xl bg-rose-50 dark:bg-rose-900/10 text-rose-600 border border-rose-100 dark:border-[#30363d] text-xs font-black text-right" data-testid="transfer-form-error">
                ⚠️ {p.error}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                p.onSubmit(false);
              }}
              className="mt-6 space-y-6 text-xs text-right"
            >
              {p.step === 1 ? (
                <div className="space-y-6">
                  {/* Progress indicator */}
                  <div className="flex justify-center items-center gap-2 mb-6" dir="rtl">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-950/20 dark:border-indigo-800">
                      <span className="w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px] font-black">1</span>
                      <span className="font-black text-[11px]">{t('ct.step1')}</span>
                    </div>
                    <div className="w-8 h-0.5 bg-slate-200 dark:bg-[#30363d]" />
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border bg-slate-50 border-slate-250 text-slate-400 dark:bg-slate-900">
                      <span className="w-5 h-5 rounded-full bg-slate-300 text-white flex items-center justify-center text-[10px] font-black">2</span>
                      <span className="font-black text-[11px]">{t('ct.step2')}</span>
                    </div>
                    {isForeignCurrency && (
                      <>
                        <div className="w-8 h-0.5 bg-slate-200 dark:bg-[#30363d]" />
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border bg-slate-50 border-slate-250 text-slate-400 dark:bg-slate-900">
                          <span className="w-5 h-5 rounded-full bg-slate-300 text-white flex items-center justify-center text-[10px] font-black">3</span>
                          <span className="font-black text-[11px]">{t('ct.step3')}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Step 1 Fields */}
                  <div className="space-y-4">
                    {/* Remittance Option */}
                    <div className="space-y-2">
                      <label className="block text-sm font-black text-slate-700 dark:text-slate-300">
                        {t('ct.settlement_type')}
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <button
                          type="button"
                          onClick={() => p.onTabChange('vault')}
                          className={`p-6 rounded-[2rem] border transition-all text-center flex flex-col items-center justify-center gap-3 ${
                            activeSubTab === 'vault'
                              ? 'bg-indigo-50 border-indigo-500 text-indigo-700 dark:bg-indigo-950/30 font-black'
                              : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 dark:bg-[#0d1117] dark:border-[#30363d] font-semibold'
                          }`}
                          data-testid="transfer-form-vault"
                        >
                          <span className="text-3xl">🏦</span>
                          <span className="font-black text-sm">{t('ct.opt_vault')}</span>
                          <span className="text-[10px] text-slate-400 font-semibold">{t('ct.opt_vault_desc')}</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => p.onTabChange('office')}
                          className={`p-6 rounded-[2rem] border transition-all text-center flex flex-col items-center justify-center gap-3 ${
                            activeSubTab === 'office'
                              ? 'bg-indigo-50 border-indigo-500 text-indigo-700 dark:bg-indigo-950/30 font-black'
                              : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 dark:bg-[#0d1117] dark:border-[#30363d] font-semibold'
                          }`}
                          data-testid="transfer-form-office"
                        >
                          <span className="text-3xl">🏢</span>
                          <span className="font-black text-sm">{t('ct.opt_office')}</span>
                          <span className="text-[10px] text-slate-400 font-semibold">{t('ct.opt_office_desc')}</span>
                        </button>
                      </div>
                    </div>

                    {/* Currency Option */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-black text-slate-700 dark:text-slate-300">
                          {t('ct.choose_currency')}
                        </label>
                        <span className="text-[11px] text-slate-400 font-bold">
                          {t('ct.shows_balance')}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-60 overflow-y-auto p-1">
                        {p.currencies.map((c) => {
                          const isSelected = currency === c.code;
                          const isZero = c.zero;
                          return (
                            <button
                              key={c.code}
                              type="button"
                              onClick={() => p.onCurrency(c.code)}
                              className={`p-3.5 rounded-2xl border transition-all flex flex-col items-center justify-center gap-1.5 font-black text-xs cursor-pointer ${
                                isSelected
                                  ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-600/20 ring-2 ring-indigo-400'
                                  : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-300 dark:bg-[#0d1117] dark:border-[#30363d] dark:text-slate-200'
                              }`}
                              data-testid={`transfer-currency-${c.code}`}
                            >
                              <span className="text-2xl">{c.flag}</span>
                              <span className="text-center truncate w-full">{c.label}</span>
                              <div className="flex flex-col items-center gap-1 w-full mt-0.5">
                                <span className={`text-[10px] font-mono px-2 py-0.5 rounded-md ${isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
                                  {c.code} ({c.symbol})
                                </span>
                                <span className={`text-[9px] font-mono px-2 py-0.5 rounded font-extrabold ${
                                  isSelected
                                    ? (isZero ? 'bg-rose-500 text-white' : 'bg-emerald-500 text-white')
                                    : (isZero ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200 dark:border-rose-900/50' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/50')
                                }`}>
                                  {t('ct.balance_short')} {c.balanceLabel}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Direction Option */}
                    <div className="space-y-2">
                      <label className="block text-sm font-black text-slate-700 dark:text-slate-300">
                        {t('ct.direction')}
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          type="button"
                          onClick={() => p.onDirection('sent')}
                          className={`p-4 rounded-xl font-black transition-all flex items-center justify-center gap-2 ${
                            p.direction === 'sent'
                              ? 'bg-indigo-600 text-white shadow-md'
                              : 'bg-slate-50 dark:bg-[#161b22] text-slate-700 dark:text-slate-300 border border-slate-100 dark:border-[#30363d]'
                          }`}
                          data-testid="transfer-direction-sent"
                        >
                          <span>{t('ct.dir_out')}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => p.onDirection('received')}
                          className={`p-4 rounded-xl font-black transition-all flex items-center justify-center gap-2 ${
                            p.direction === 'received'
                              ? 'bg-teal-600 text-white shadow-md'
                              : 'bg-slate-50 dark:bg-[#161b22] text-slate-700 dark:text-slate-300 border border-slate-100 dark:border-[#30363d]'
                          }`}
                          data-testid="transfer-direction-received"
                        >
                          <span>{t('ct.dir_in')}</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Step 1 Actions */}
                  <div className="pt-4 border-t border-slate-100 dark:border-[#30363d] grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => p.onStep(2)}
                      className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-black rounded-2xl transition-all shadow-lg text-xs flex items-center justify-center gap-1.5 active:scale-95"
                      data-testid="transfer-step1-next"
                    >
                      {t('ct.next')}
                    </button>
                    <button
                      type="button"
                      onClick={p.onClose}
                      className="w-full py-4 bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] hover:bg-gray-100 dark:hover:bg-[#21262d] rounded-2xl font-black text-slate-700 dark:text-slate-300 transition-all text-xs flex items-center justify-center gap-1.5 active:scale-95"
                    >
                      {t('ct.cancel_close')}
                    </button>
                  </div>
                </div>
              ) : p.step === 2 ? (
                <div className="space-y-6">
                  {/* Progress indicator */}
                  <div className="flex justify-center items-center gap-2 mb-6" dir="rtl">
                    <button
                      type="button"
                      onClick={() => p.onStep(1)}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-xl border bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-800 cursor-pointer hover:scale-105 transition-all"
                    >
                      <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-black">✓</span>
                      <span className="font-black text-[11px]">{t('ct.step1')}</span>
                    </button>
                    <div className="w-8 h-0.5 bg-emerald-300 dark:bg-emerald-800" />
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-950/20 dark:border-indigo-800">
                      <span className="w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px] font-black">2</span>
                      <span className="font-black text-[11px]">{t('ct.step2')}</span>
                    </div>
                    {isForeignCurrency && (
                      <>
                        <div className="w-8 h-0.5 bg-slate-200 dark:bg-[#30363d]" />
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border bg-slate-50 border-slate-250 text-slate-400 dark:bg-slate-900">
                          <span className="w-5 h-5 rounded-full bg-slate-300 text-white flex items-center justify-center text-[10px] font-black">3</span>
                          <span className="font-black text-[11px]">{t('ct.step3')}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Step 2 Fields */}
                  {/* 1. Client and Core data */}
                  <div className="p-5 bg-slate-50/50 dark:bg-[#161b22]/20 border border-slate-100 dark:border-[#30363d] rounded-2xl text-right">
                    <div className="text-slate-900 dark:text-white font-extrabold text-[11px] mb-3 border-b pb-1 border-slate-100 dark:border-[#30363d]">
                      {t('ct.section_person')}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="block font-black text-slate-700 dark:text-slate-300">{t('ct.person_name')}</label>
                        <input
                          type="text"
                          value={v.clientName || ''}
                          onChange={(e) => p.onChange('clientName', e.target.value)}
                          className="w-full px-4 py-3 bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-extrabold text-slate-800 dark:text-slate-100"
                          placeholder={t('ct.person_name_ph')}
                          data-testid="transfer-client-name"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="block font-black text-slate-700 dark:text-slate-300">{t('ct.doc_no')}</label>
                        <input
                          type="text"
                          value={v.clientDocument || ''}
                          onChange={(e) => p.onChange('clientDocument', e.target.value)}
                          className="w-full px-4 py-3 bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-extrabold text-slate-800 dark:text-slate-100"
                          placeholder={t('ct.doc_ph')}
                          data-testid="transfer-client-document"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="block font-black text-slate-700 dark:text-slate-300">{t('ct.destination')}</label>
                        <input
                          type="text"
                          value={v.destination || ''}
                          onChange={(e) => p.onChange('destination', e.target.value)}
                          className="w-full px-4 py-3 bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-extrabold text-slate-800 dark:text-slate-100"
                          placeholder={t('ct.destination_ph')}
                          data-testid="transfer-destination"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="block font-black text-slate-700 dark:text-slate-300">{t('ct.code')}</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={v.code || ''}
                            readOnly={!p.codeEditable}
                            onChange={(e) => p.onChange('code', e.target.value)}
                            className="w-full pl-4 pr-14 py-3 bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono font-black text-slate-800 dark:text-slate-100"
                            placeholder="FLS-XXXXXXXX"
                            data-testid="transfer-code"
                          />
                          {!p.codeEditable && (
                            <button
                              type="button"
                              onClick={p.onGenerate}
                              className="absolute left-3 top-1/2 -translate-y-1/2 px-2.5 py-1 text-[10px] font-black bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400 rounded-lg"
                              data-testid="transfer-code-generate"
                            >
                              ⚡ {t('ct.generate')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Accounting/Payment selection */}
                  <div className="p-4 border border-slate-150 dark:border-[#30363d] rounded-2xl space-y-3 bg-slate-50/20 text-right">
                    <div className="text-slate-900 dark:text-white font-extrabold text-[11px] mb-1 border-b pb-1 border-slate-100 dark:border-[#30363d]">
                      {t('ct.section_payment')}
                    </div>
                    <div className="space-y-1.5">
                      <label className="block font-black text-slate-700 dark:text-slate-300">{t('ct.payment_type')}</label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {(['cash', 'credit', 'trust'] as const).map((k) => (
                          <button
                            key={k}
                            type="button"
                            onClick={() => p.onPayment(k)}
                            className={`py-3 rounded-xl text-[10px] font-black transition-all ${p.payment === k ? 'bg-indigo-600 text-white shadow' : 'bg-slate-50 dark:bg-[#161b22] text-slate-700 dark:text-[#8b949e] border border-slate-100 dark:border-[#30363d]'}`}
                            data-testid={`transfer-payment-${k}`}
                          >
                            {t(`ct.pay_btn_${k}`)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3.5">
                      {/* Target client account picker for DEFERRED / TRUST */}
                      {(p.payment === 'credit' || p.payment === 'trust') && (
                        <div className="p-4 bg-indigo-50/30 dark:bg-indigo-950/5 border border-indigo-100/50 dark:border-[#30363d] rounded-2xl">
                          <label className="block font-black text-indigo-950 dark:text-indigo-400 mb-1.5">{t('ct.select_client')}</label>
                          <select
                            value={p.customerId || ''}
                            onChange={(e) => p.onCustomer(e.target.value)}
                            className="w-full px-4 py-3 bg-white dark:bg-[#05070a] border border-indigo-150 dark:border-[#30363d] rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-extrabold text-slate-800 dark:text-slate-100 mb-2"
                            data-testid="transfer-customer"
                          >
                            <option value="">{t('ct.select_client_ph')}</option>
                            {p.customerOptions.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            placeholder={t('ct.client_search_ph')}
                            className="w-full px-3 py-1.5 text-xs bg-white dark:bg-[#0d1117] border border-indigo-100 dark:border-[#30363d] rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 font-bold text-slate-700 dark:text-slate-205"
                            onChange={(e) => p.onCustomerSearch(e.target.value)}
                          />
                        </div>
                      )}

                      {/* Partner office account picker always shown in tab office or when partnerFee is specified */}
                      {p.showPartner && (
                        <div className="p-4 bg-indigo-50/50 dark:bg-indigo-950/10 border border-indigo-100/70 dark:border-[#30363d] rounded-2xl animate-fadeIn">
                          <label className="block font-black text-indigo-900 dark:text-indigo-400 mb-2">{t('ct.select_partner')}</label>
                          <PartnerOfficeSelector {...p.partner} />
                          {p.partnerIncoming && (
                            <div className="mt-3 space-y-1.5" data-testid="transfer-commission-payer">
                              <label className="block font-black text-indigo-900 dark:text-indigo-400">{t('ct.payer_label')}</label>
                              <div className="grid grid-cols-2 gap-1.5">
                                {(['partner', 'beneficiary'] as const).map((k) => (
                                  <button
                                    key={k}
                                    type="button"
                                    onClick={() => p.onCommissionPayer(k)}
                                    className={`py-3 rounded-xl text-[10px] font-black transition-all ${p.commissionPayer === k ? 'bg-indigo-600 text-white shadow' : 'bg-slate-50 dark:bg-[#161b22] text-slate-700 dark:text-[#8b949e] border border-slate-100 dark:border-[#30363d]'}`}
                                    data-testid={`transfer-payer-${k}`}
                                  >
                                    {t(`ct.payer_${k}`)}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 3. Financial math fields with TWO-WAY validation */}
                  <div className="p-6 border border-slate-100 dark:border-[#30363d] bg-slate-50/50 dark:bg-[#161b22]/10 rounded-3xl space-y-5 text-right relative overflow-hidden animate-fadeIn">
                    {/* Decorative Accent Header */}
                    <div className="flex items-center justify-between border-b pb-3 border-slate-100 dark:border-[#30363d]">
                      <span className="text-slate-900 dark:text-white font-extrabold text-xs flex items-center gap-2">
                        💰 {t('ct.section_money')}
                      </span>
                      <div className="h-1 w-20 bg-indigo-500 rounded-full"></div>
                    </div>

                    {/* Unified Horizontal & Stacked Layout for massive data display */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                      {/* Left Side: Principal Amount & (Commission Rate / Partner Fee) separate boxes */}
                      <div className="lg:col-span-1 space-y-4">
                        {/* 1. Original Principal */}
                        <div className="space-y-1.5 flex flex-col">
                          <label className="block font-black text-slate-700 dark:text-slate-300 text-xs text-right pr-1">
                            {t('ct.amount')} ({currency})
                          </label>
                          <input
                            type="text"
                            value={formatInputWithCommas(v.amount)}
                            onChange={typed('amount')}
                            style={getDynamicFontSizeStyle(formatInputWithCommas(v.amount), 22)}
                            className="w-full h-20 px-5 bg-white dark:bg-[#05070a] border-2 border-slate-200 dark:border-slate-800 rounded-2xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/15 font-black text-slate-900 dark:text-white font-mono text-left transition-all"
                            placeholder="0.00"
                            id="form-amount-principal"
                            data-testid="transfer-amount"
                          />
                        </div>

                        {/* 2 & 4. Separate, independent input fields side-by-side */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* 2. Interactive Commission Rate */}
                          <div className="space-y-1.5 flex flex-col">
                            <label className="block font-black text-indigo-600 dark:text-indigo-400 text-xs text-right pr-1 truncate font-sans">
                              {currency === 'IQD' ? t('ct.rate_per_million') : t('ct.rate_per_thousand')}
                            </label>
                            <input
                              type="text"
                              value={formatInputWithCommas(v.commissionRate)}
                              onChange={typed('commissionRate')}
                              style={getDynamicFontSizeStyle(formatInputWithCommas(v.commissionRate), 18)}
                              className="w-full h-20 px-4 bg-white dark:bg-[#05070a] border-2 border-slate-200 dark:border-slate-800 rounded-2xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/15 font-bold text-indigo-700 dark:text-indigo-400 font-mono text-left transition-all"
                              placeholder={currency === 'IQD' ? '1,000' : '10'}
                              id="form-amount-rate"
                              data-testid="transfer-rate"
                            />
                          </div>

                          {/* 4. Second Office Fee - Always visible next to Commission Rate */}
                          <div className="space-y-1.5 flex flex-col">
                            <label className="block font-black text-amber-600 dark:text-amber-400 text-xs text-right pr-1 truncate font-sans">
                              {p.partnerIncoming ? t('ct.partner_fee_in') : t('ct.partner_fee')}
                            </label>
                            <input
                              type="text"
                              value={formatInputWithCommas(v.partnerFee)}
                              onChange={typed('partnerFee')}
                              style={getDynamicFontSizeStyle(formatInputWithCommas(v.partnerFee), 18)}
                              className="w-full h-20 px-4 bg-white dark:bg-[#05070a] border-2 border-slate-200 dark:border-slate-800 rounded-2xl focus:border-amber-500 focus:ring-4 focus:ring-amber-500/15 font-bold text-amber-600 dark:text-amber-400 font-mono text-left transition-all"
                              placeholder="0.00"
                              id="form-amount-partner-fee"
                              data-testid="transfer-fee"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Right Side: Major fields (Calculated Commission & Total Cash Amount) stretched to the max */}
                      <div className="lg:col-span-1 space-y-4">
                        {/* 3. Calculated Commission */}
                        <div className="space-y-1.5 flex flex-col">
                          <label className="block font-black text-slate-700 dark:text-slate-300 text-xs text-right pr-1 font-sans">
                            {t('ct.commission')} ({currency})
                          </label>
                          <input
                            type="text"
                            value={formatInputWithCommas(v.commission)}
                            onChange={typed('commission')}
                            style={getDynamicFontSizeStyle(formatInputWithCommas(v.commission), 22)}
                            className="w-full h-20 px-5 bg-white dark:bg-[#05070a] border-2 border-emerald-500/25 dark:border-emerald-450/20 rounded-2xl focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/15 font-bold text-emerald-600 dark:text-emerald-400 font-mono text-left transition-all"
                            placeholder="0.00"
                            id="form-amount-commission"
                            data-testid="transfer-commission"
                          />
                        </div>

                        {/* 5. Combined aggregate total (Total cash) */}
                        <div className="space-y-1.5 flex flex-col">
                          <label className="block font-black text-indigo-700 dark:text-indigo-400 text-xs text-right pr-1 font-sans">
                            {t('ct.total')} ({currency})
                          </label>
                          <input
                            type="text"
                            value={formatInputWithCommas(v.totalAmount)}
                            onChange={typed('totalAmount')}
                            style={getDynamicFontSizeStyle(formatInputWithCommas(v.totalAmount), 22)}
                            className="w-full h-20 px-5 bg-indigo-50/50 text-indigo-950 dark:bg-indigo-950/20 dark:text-indigo-200 border-2 border-indigo-200/30 dark:border-indigo-400/25 rounded-2xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/15 font-black font-mono text-left transition-all"
                            placeholder="0.00"
                            id="form-amount-total"
                            data-testid="transfer-total"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Line horizontal separation decor at bottom of calculating section */}
                    <div className="border-t border-slate-100 dark:border-[#30363d] my-1"></div>

                    {/* Live Currency Balance Status Alert */}
                    {p.balanceAlert && p.balanceAlert.state === 'zero' && (
                      <div className="p-4 bg-rose-50 dark:bg-rose-950/30 border-2 border-rose-300 dark:border-rose-800 rounded-2xl flex items-start gap-3 text-right" data-testid="transfer-alert-zero">
                        <AlertTriangle className="text-rose-600 flex-shrink-0 mt-0.5" size={20} />
                        <div className="space-y-1 text-xs flex-1">
                          <div className="font-black text-rose-700 dark:text-rose-300 flex items-center justify-between">
                            <span>{t('ct.alert_zero_title', { currency: p.balanceAlert.currencyName })}</span>
                            <span className="font-mono bg-rose-200 dark:bg-rose-900 text-rose-900 dark:text-rose-100 px-2 py-0.5 rounded text-[10px] font-black">
                              0.00 {p.balanceAlert.symbol}
                            </span>
                          </div>
                          <p className="text-[11px] text-rose-600 dark:text-rose-400 font-semibold leading-relaxed">
                            {t('ct.alert_zero_body')}
                          </p>
                        </div>
                      </div>
                    )}
                    {p.balanceAlert && p.balanceAlert.state === 'deficit' && (
                      <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-300 dark:border-amber-700 rounded-2xl flex items-start gap-3 text-right" data-testid="transfer-alert-deficit">
                        <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                        <div className="space-y-1 text-xs flex-1">
                          <div className="font-black text-amber-800 dark:text-amber-300 flex items-center justify-between">
                            <span>{t('ct.alert_deficit_title')}</span>
                            <span className="font-mono bg-amber-200 dark:bg-amber-900 text-amber-950 dark:text-amber-100 px-2 py-0.5 rounded text-[10px] font-black">
                              {p.balanceAlert.balanceLabel} / {p.balanceAlert.requiredLabel} {p.balanceAlert.symbol}
                            </span>
                          </div>
                          <p className="text-[11px] text-amber-700 dark:text-amber-400 font-semibold leading-relaxed">
                            {t('ct.alert_deficit_body', { available: p.balanceAlert.balanceLabel, required: p.balanceAlert.requiredLabel, deficit: p.balanceAlert.deficitLabel, symbol: p.balanceAlert.symbol })}
                          </p>
                        </div>
                      </div>
                    )}
                    {p.balanceAlert && p.balanceAlert.state === 'ok' && (
                      <div className="p-3.5 bg-emerald-50/70 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-900/40 rounded-2xl flex items-center justify-between text-xs text-emerald-800 dark:text-emerald-300 font-bold text-right" data-testid="transfer-alert-ok">
                        <span className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                          {t('ct.alert_ok', { currency: p.balanceAlert.currencyName })}
                        </span>
                        <span className="font-mono font-black text-emerald-700 dark:text-emerald-300 text-sm">
                          {p.balanceAlert.balanceLabel} {p.balanceAlert.symbol}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Step 2 Actions */}
                  <div className="pt-4 border-t border-slate-100 dark:border-[#30363d]">
                    {isForeignCurrency ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => p.onStep(1)}
                          className="w-full py-4 bg-gray-55 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] hover:bg-gray-100 dark:hover:bg-[#21262d] rounded-2xl font-black text-slate-700 dark:text-slate-300 transition-all text-xs flex items-center justify-center gap-1.5 active:scale-95 shadow-sm"
                        >
                          {t('ct.back')}
                        </button>
                        <button
                          type="button"
                          onClick={p.onNext}
                          disabled={p.busy}
                          className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-black rounded-2xl transition-all shadow-lg text-xs flex items-center justify-center gap-1.5 active:scale-95"
                          data-testid="transfer-step2-next"
                        >
                          <span>{t('ct.next_settlement')}</span>
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <button
                          type="button"
                          onClick={() => p.onStep(1)}
                          className="w-full py-4 bg-gray-55 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] hover:bg-gray-100 dark:hover:bg-[#21262d] rounded-2xl font-black text-slate-700 dark:text-slate-300 transition-all text-xs flex items-center justify-center gap-1.5 active:scale-95 shadow-sm"
                        >
                          {t('ct.back')}
                        </button>
                        <button
                          type="submit"
                          disabled={p.busy}
                          className="w-full py-4 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-650 text-white font-black rounded-2xl transition-all shadow-md text-xs flex items-center justify-center gap-1.5 active:scale-95 border border-slate-700/30"
                          id="submit-form-btn"
                          data-testid="transfer-submit"
                        >
                          <span>💾</span>
                          {t('ct.record')}
                        </button>
                        <button
                          type="button"
                          disabled={p.busy}
                          onClick={() => p.onSubmit(true)}
                          className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-black rounded-2xl transition-all shadow-md text-xs flex items-center justify-center gap-1.5 active:scale-95"
                          data-testid="transfer-submit-print"
                        >
                          <Printer size={16} />
                          {t('ct.record_print')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-6 animate-fadeIn">
                  {/* Progress indicator for Step 3 */}
                  <div className="flex justify-center items-center gap-2 mb-6" dir="rtl">
                    <button
                      type="button"
                      onClick={() => p.onStep(1)}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-xl border bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-800 cursor-pointer hover:scale-105 transition-all"
                    >
                      <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-black">✓</span>
                      <span className="font-black text-[11px]">{t('ct.step1')}</span>
                    </button>
                    <div className="w-8 h-0.5 bg-emerald-300 dark:bg-emerald-800" />
                    <button
                      type="button"
                      onClick={() => p.onStep(2)}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-xl border bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-800 cursor-pointer hover:scale-105 transition-all"
                    >
                      <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-black">✓</span>
                      <span className="font-black text-[11px]">{t('ct.step2_short')}</span>
                    </button>
                    <div className="w-8 h-0.5 bg-indigo-300 dark:bg-indigo-800" />
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-950/20 dark:border-indigo-800">
                      <span className="w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px] font-black">3</span>
                      <span className="font-black text-[11px]">{t('ct.step3')}</span>
                    </div>
                  </div>

                  {/* Step 3 Banner & Guidance */}
                  <div className="p-5 rounded-3xl bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-900 text-white border border-indigo-500/20 shadow-lg space-y-2 text-right">
                    <div className="flex items-center justify-between">
                      <span className="px-3 py-1 bg-indigo-500/20 text-indigo-300 border border-indigo-400/25 rounded-full text-[10px] font-black">
                        🏦 {t('ct.stage3_badge')}
                      </span>
                      <span className="text-xl">💰</span>
                    </div>
                    <h4 className="text-sm font-black text-white">
                      {t('ct.stage3_title', { currency })}
                    </h4>
                    <p className="text-[10px] text-slate-300 font-medium leading-relaxed">
                      {t('ct.stage3_desc')}
                    </p>
                  </div>

                  {/* Vault Settlement Currency Selection: IQD vs USD (+ the currency itself, D-085 (b)) */}
                  <div className="space-y-2">
                    <label className="block text-xs font-black text-slate-700 dark:text-slate-300 text-right">
                      {t('ct.settle_choose')}
                    </label>
                    <div className={`grid grid-cols-1 ${p.usdSettlementAvailable ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4`}>
                      {/* IQD Option */}
                      <button
                        type="button"
                        onClick={() => p.onSettlement('IQD')}
                        className={`p-5 rounded-3xl border-2 transition-all flex flex-col items-center justify-center gap-2.5 text-center cursor-pointer ${
                          p.settlement === 'IQD'
                            ? 'bg-emerald-50/70 border-emerald-500 text-emerald-950 dark:bg-emerald-950/30 dark:border-emerald-500 dark:text-emerald-200 shadow-md ring-2 ring-emerald-400'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 dark:bg-[#0d1117] dark:border-[#30363d] dark:text-slate-300'
                        }`}
                        data-testid="transfer-settle-IQD"
                      >
                        <span className="text-3xl">🇮🇶</span>
                        <div>
                          <span className="font-black text-sm block">{t('ct.settle_iqd')}</span>
                          <span className="text-[10px] text-slate-400 font-semibold block mt-0.5">
                            {t('ct.settle_iqd_desc')}
                          </span>
                        </div>
                        <span className="text-[10px] font-mono px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 font-black">
                          {t('ct.vault_balance')} {p.iqdVaultLabel} د.ع
                        </span>
                      </button>

                      {/* USD Option */}
                      {p.usdSettlementAvailable && (
                        <button
                          type="button"
                          onClick={() => p.onSettlement('USD')}
                          className={`p-5 rounded-3xl border-2 transition-all flex flex-col items-center justify-center gap-2.5 text-center cursor-pointer ${
                            p.settlement === 'USD'
                              ? 'bg-amber-50/70 border-amber-500 text-amber-950 dark:bg-amber-950/30 dark:border-amber-500 dark:text-amber-200 shadow-md ring-2 ring-amber-400'
                              : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 dark:bg-[#0d1117] dark:border-[#30363d] dark:text-slate-300'
                          }`}
                          data-testid="transfer-settle-USD"
                        >
                          <span className="text-3xl">🇺🇸</span>
                          <div>
                            <span className="font-black text-sm block">{t('ct.settle_usd')}</span>
                            <span className="text-[10px] text-slate-400 font-semibold block mt-0.5">
                              {t('ct.settle_usd_desc')}
                            </span>
                          </div>
                          <span className="text-[10px] font-mono px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 font-black">
                            {t('ct.vault_balance')} {p.usdVaultLabel} $
                          </span>
                        </button>
                      )}

                      {/* The currency itself (D-085 (b), new) */}
                      <button
                        type="button"
                        onClick={() => p.onSettlement('SELF')}
                        className={`p-5 rounded-3xl border-2 transition-all flex flex-col items-center justify-center gap-2.5 text-center cursor-pointer ${
                          p.settlement === 'SELF'
                            ? 'bg-indigo-50/70 border-indigo-500 text-indigo-950 dark:bg-indigo-950/30 dark:border-indigo-500 dark:text-indigo-200 shadow-md ring-2 ring-indigo-400'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 dark:bg-[#0d1117] dark:border-[#30363d] dark:text-slate-300'
                        }`}
                        data-testid="transfer-settle-SELF"
                      >
                        <span className="text-3xl">{p.currencies.find((c) => c.code === currency)?.flag ?? '🪙'}</span>
                        <div>
                          <span className="font-black text-sm block">{t('ct.settle_self', { currency })}</span>
                          <span className="text-[10px] text-slate-400 font-semibold block mt-0.5">
                            {t('ct.settle_self_desc')}
                          </span>
                        </div>
                        <span className="text-[10px] font-mono px-3 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300 font-black">
                          {t('ct.vault_balance')} {p.selfStockLabel}
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Rate and Equivalent Cash Calculations Box */}
                  <div className="p-6 bg-slate-50/70 dark:bg-[#161b22]/30 border border-slate-100 dark:border-[#30363d] rounded-3xl space-y-4 text-right">
                    <div className="flex items-center justify-between border-b pb-3 border-slate-100 dark:border-[#30363d]">
                      <span className="text-slate-900 dark:text-white font-extrabold text-xs flex items-center gap-2">
                        🔄 {t('ct.rate_box')}
                      </span>
                      <span className="text-[10px] text-indigo-600 dark:text-indigo-400 font-black" data-testid="transfer-rate-header">
                        {p.rateHeader}
                      </span>
                    </div>

                    {p.settlement === 'SELF' ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5 flex flex-col">
                          <label className="block font-black text-indigo-700 dark:text-indigo-400 text-xs text-right pr-1">
                            {t('ct.valuation_rate', { unit: p.quoteUnitLabel, currency })}
                          </label>
                          <input
                            type="text"
                            value={formatInputWithCommas(v.valuationRate)}
                            onChange={typed('valuationRate')}
                            style={getDynamicFontSizeStyle(formatInputWithCommas(v.valuationRate), 20)}
                            className="w-full h-16 px-4 bg-white dark:bg-[#05070a] border-2 border-indigo-200 dark:border-indigo-900 rounded-2xl focus:border-indigo-500 font-bold text-indigo-700 dark:text-indigo-400 font-mono text-left"
                            placeholder="0.00"
                            data-testid="transfer-valuation-rate"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Exchange Rate Input */}
                        <div className="space-y-1.5 flex flex-col">
                          <label className="block font-black text-indigo-700 dark:text-indigo-400 text-xs text-right pr-1">
                            {t('ct.rate_label', { unit: p.quoteUnitLabel, currency, target: p.settlement === 'IQD' ? t('ct.unit_iqd') : t('ct.unit_usd') })}
                          </label>
                          <input
                            type="text"
                            value={formatInputWithCommas(v.settlementRate)}
                            onChange={typed('settlementRate')}
                            style={getDynamicFontSizeStyle(formatInputWithCommas(v.settlementRate), 20)}
                            className="w-full h-16 px-4 bg-white dark:bg-[#05070a] border-2 border-indigo-200 dark:border-indigo-900 rounded-2xl focus:border-indigo-500 font-bold text-indigo-700 dark:text-indigo-400 font-mono text-left"
                            placeholder="0.00"
                            id="form-settlement-rate"
                            data-testid="transfer-settlement-rate"
                          />
                        </div>

                        {/* Converted Cash Input */}
                        <div className="space-y-1.5 flex flex-col">
                          <label className="block font-black text-emerald-700 dark:text-emerald-400 text-xs text-right pr-1">
                            {t('ct.cash_label', { sym: settleSym })}
                          </label>
                          <input
                            type="text"
                            value={formatInputWithCommas(v.settlementCash)}
                            onChange={typed('settlementCash')}
                            style={getDynamicFontSizeStyle(formatInputWithCommas(v.settlementCash), 20)}
                            className="w-full h-16 px-4 bg-white dark:bg-[#05070a] border-2 border-emerald-300 dark:border-emerald-800 rounded-2xl focus:border-emerald-500 font-black text-emerald-600 dark:text-emerald-400 font-mono text-left"
                            placeholder="0.00"
                            id="form-settlement-cash"
                            data-testid="transfer-settlement-cash"
                          />
                        </div>

                        {p.showCost && (
                          <div className="space-y-1.5 flex flex-col md:col-span-2">
                            <label className="block font-black text-slate-700 dark:text-slate-300 text-xs text-right pr-1">
                              {t('ct.cost_rate', { unit: p.quoteUnitLabel, currency, target: p.settlement === 'IQD' ? t('ct.unit_iqd') : t('ct.unit_usd') })}
                            </label>
                            <input
                              type="text"
                              value={formatInputWithCommas(v.costRate)}
                              onChange={typed('costRate')}
                              className="w-full h-12 px-4 bg-white dark:bg-[#05070a] border-2 border-slate-200 dark:border-slate-800 rounded-2xl focus:border-indigo-500 font-bold text-slate-700 dark:text-slate-300 font-mono text-left"
                              placeholder="0.00"
                              data-testid="transfer-cost-rate"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Summary Breakdown Grid */}
                    <div className="p-4 rounded-2xl bg-white dark:bg-[#0d1117] border border-slate-100 dark:border-[#30363d] space-y-2 text-xs">
                      <div className="flex justify-between items-center text-slate-500">
                        <span>{t('ct.sum_principal')}</span>
                        <span className="font-mono font-black text-slate-800 dark:text-slate-200">
                          {p.summary.principalLabel}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-slate-500">
                        <span>{t('ct.sum_commission')}</span>
                        <span className="font-mono font-black text-emerald-600 text-right">
                          <div>+{p.summary.commissionLabel}</div>
                          {p.summary.commissionConvertedLabel !== null && (
                            <div className="text-[10px] text-indigo-600 dark:text-indigo-400 font-bold mt-0.5">
                              {t('ct.sum_converted')} {p.summary.commissionConvertedLabel}
                            </div>
                          )}
                        </span>
                      </div>
                      {p.summary.partnerFeeLabel !== null && (
                        <div className="flex justify-between items-center text-slate-500">
                          <span>{t('ct.sum_fee')}</span>
                          <span className="font-mono font-black text-amber-600">
                            +{p.summary.partnerFeeLabel}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center text-slate-700 dark:text-slate-300 font-extrabold border-t pt-2 border-slate-100 dark:border-[#30363d]">
                        <span>{t('ct.sum_total')}</span>
                        <span className="font-mono text-indigo-600 dark:text-indigo-400 font-black">
                          {p.summary.totalLabel}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-emerald-700 dark:text-emerald-400 font-black border-t-2 border-dashed pt-2 border-emerald-100 dark:border-emerald-950/40 text-sm">
                        <span>
                          {p.direction === 'sent' ? t('ct.sum_in') : t('ct.sum_out')}
                        </span>
                        <span className="font-mono text-base font-black" data-testid="transfer-cash-flow">
                          {p.summary.cashFlowLabel}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Step 3 Actions */}
                  <div className="pt-4 border-t border-slate-100 dark:border-[#30363d] grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => p.onStep(2)}
                      className="w-full py-4 bg-gray-55 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] hover:bg-gray-100 dark:hover:bg-[#21262d] rounded-2xl font-black text-slate-700 dark:text-slate-300 transition-all text-xs flex items-center justify-center gap-1.5 active:scale-95 shadow-sm"
                    >
                      {t('ct.back_details')}
                    </button>
                    <button
                      type="submit"
                      disabled={p.busy}
                      className="w-full py-4 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-650 text-white font-black rounded-2xl transition-all shadow-md text-xs flex items-center justify-center gap-1.5 active:scale-95 border border-slate-700/30"
                      id="submit-form-btn-step3"
                      data-testid="transfer-submit"
                    >
                      <span>💾</span>
                      {t('ct.record')}
                    </button>
                    <button
                      type="button"
                      disabled={p.busy}
                      onClick={() => p.onSubmit(true)}
                      className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-black rounded-2xl transition-all shadow-md text-xs flex items-center justify-center gap-1.5 active:scale-95"
                      data-testid="transfer-submit-print"
                    >
                      <Printer size={16} />
                      {t('ct.record_print')}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
